/**
 * AbilityEditorTabs — Comprehensive ability designer with 8 tabs.
 *
 * Tabs:
 * 1. Stats    — mana, cooldown, cast time
 * 2. Projectile — shape, size, color, stroke, gravity, speed, homing, lifespan
 * 3. Behavior — AOE/channel/buff/dash params (read from tags + metadata)
 * 4. Compose  — copy from other abilities, modifiers, flags
 * 5. Timeline — animation timeline scheduling
 * 6. VFX      — per-primitive editing with tweens, scatter, orbit
 * 7. Particles — emitter settings
 * 8. Preview  — play ability/VFX/projectile, export/import JSON
 */

import React, { useState } from 'react';
import { AbilityMetadata, abilityRegistry } from '../../systems/player/abilities/AbilityRegistry';
import { VFXEffectRegistry } from '../../effects/VFXEffectRegistry';
import { useGame } from '../contexts/GameContext';
import {
  StatsTab,
  ProjectileTab,
  BehaviorTab,
  ComposeTab,
  TimelineTab,
  VfxTab,
  ParticlesTab,
  PreviewTab,
  TABS,
  TabsContainer,
  TabBar,
  TabButton,
  TabContent,
} from './AbilityEditor/tabs';
import type { TabId } from './AbilityEditor/tabs';

// ── Component ──────────────────────────────────────────────────

interface EditorTabsProps {
  ability: AbilityMetadata;
  onCastAbility?: (abilityId: string) => void;
}

export const AbilityEditorTabs: React.FC<EditorTabsProps> = ({ ability, onCastAbility }) => {
  const [activeTab, setActiveTab] = useState<TabId>('stats');
  const [importJson, setImportJson] = useState('');
  const [composeSourceId, setComposeSourceId] = useState('');
  const { player } = useGame();

  const update = (patch: any) => abilityRegistry.updateMetadata(ability.id, patch);

  const getVfxId = () => ability.vfx?.muzzleFlash || ability.vfx?.windUp || ability.id;
  const getVfxConfig = () => VFXEffectRegistry.get(getVfxId());

  return (
    <TabsContainer>
      <TabBar>
        {TABS.map(tab => (
          <TabButton key={tab.id} $active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </TabButton>
        ))}
      </TabBar>

      <TabContent>
        {activeTab === 'stats' && <StatsTab ability={ability} update={update} />}
        {activeTab === 'projectile' && <ProjectileTab ability={ability} update={update} />}
        {activeTab === 'behavior' && <BehaviorTab ability={ability} update={update} />}
        {activeTab === 'compose' && <ComposeTab ability={ability} update={update} composeSourceId={composeSourceId} setComposeSourceId={setComposeSourceId} />}
        {activeTab === 'timeline' && <TimelineTab ability={ability} update={update} player={player} />}
        {activeTab === 'vfx' && <VfxTab ability={ability} update={update} player={player} getVfxId={getVfxId} getVfxConfig={getVfxConfig} />}
        {activeTab === 'particles' && <ParticlesTab ability={ability} update={update} player={player} />}
        {activeTab === 'preview' && <PreviewTab ability={ability} update={update} onCastAbility={onCastAbility} importJson={importJson} setImportJson={setImportJson} player={player} getVfxId={getVfxId} />}
      </TabContent>
    </TabsContainer>
  );
};
